// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { cartPageTemplateBreakpoint } from "@flip/App/Components/Cart/CartPageTemplate";
import { constants } from "@flip/XDL/constants";
import { color, xScreen } from "@flip/XDL/Utilities";
import { mergeWithCss } from "@flip/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";

export interface CartLineContainerProps {
    extendedStyles?: CartLineContainerStyles;
    [key: string]: any;
}

interface OwnProps {
    Image: React.FC;
    Description: React.FC;
    Price: React.FC;
    Subtotal: React.FC;
    Qty: React.FC;
    Total: React.FC;
    Availability: React.FC;
    AvailabilityLink: React.FC;
    AddToList: React.FC;
    Remove: React.FC;
    LineNotesLink: React.FC;
    LineNotes: React.FC;
    Errors: React.FC;
    MinimumOrderQty: React.FC;
    MultipleSaleQty: React.FC;
}

export interface CartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    description?: InjectableCss;
    price?: InjectableCss;
    subtotal?: InjectableCss;
    qty?: InjectableCss;
    total?: InjectableCss;
    availability?: InjectableCss;
    availabilityLink?: InjectableCss;
    addToList?: InjectableCss;
    remove?: InjectableCss;
    lineNotesLink?: InjectableCss;
    lineNotes?: InjectableCss;
    topSection?: InjectableCss;
    leftSide?: InjectableCss;
    leftSideInfo?: InjectableCss;
    leftSideActions?: InjectableCss;
    rightSide?: InjectableCss;
    notesFieldSection?: InjectableCss;
    priceSection?: InjectableCss;
    rightSideActions?: InjectableCss;
    errorsSection?: InjectableCss;
    minimumOrderQty?: InjectableCss;
    multipleSaleQty?: InjectableCss;
    qtyInfoWrapper?: InjectableCss;
}

interface CartLineContainerContextType {
    styles: CartLineContainerStyles;
}

const CartLineContainerContext = React.createContext<CartLineContainerContextType>({
    styles: {},
});

export const useCartLineContainerContext = () => {
    const context = React.useContext(CartLineContainerContext);
    if (!context) {
        throw new Error("CartPageTemplate compound components cannot be rendered outside CartPageTemplate");
    }
    return context;
};
const containerClassName = "cartline-container";

const sharedNotesFieldSectionStyles: InjectableCss = {
    css: css`
        display: flex;
        width: 100%;
        max-width: 740px;
        margin-top: 8px;
        gap: 22px;
        &:empty {
            display: none;
        }

        &:before {
            content: " ";
            width: 100%;
            max-width: 91px;
        }
        ${xScreen("lte", constants.cartLine.breakpoints.md)} {
            &:before {
                display: none;
            }
        }
    `,
};

export const cartlineContainerStyles: CartLineContainerStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 22px 12px;

            &:not(.last-item) {
                border-bottom: 1px solid ${color("common.border")};
            }
            position: relative;

            &.${containerClassName}-mobile {
                display: none;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                padding: 22px 12px;
                &.${containerClassName}-desktop {
                    display: none;
                }
                &.${containerClassName}-mobile {
                    display: flex;
                }
            }

            ${xScreen("lte", cartPageTemplateBreakpoint)} {
                padding: 22px 12px;
            }

            @media print {
                display: block;
                break-inside: avoid;
            }
        `,
    },
    image: {
        css: css`
            width: 91px;
            height: auto;
            flex: 0 0 91px;
            ${xScreen("lte", "md")} {
                height: 91px;
            }
        `,
    },

    topSection: {
        css: css`
            display: flex;
            gap: 24px;
            width: 100%;
            /* XNG-Changes: FLIP-938 */
            @media print {
                flex-direction: column;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                flex-direction: column;
            }
        `,
    },
    leftSide: {
        css: css`
            display: flex;
            width: 100%;
            max-width: 450px;
            gap: 22px;
            ${({ isFullWidth }: any) => css`
                ${isFullWidth &&
                css`
                    flex: 1;
                    max-width: 100%;
                `};
            `}
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                gap: 12px;
                margin-bottom: 18px;
            }
        `,
    },
    leftSideInfo: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            ${({ isFullWidth }: any) => css`
                ${isFullWidth &&
                css`
                    flex: 1;
                    max-width: 490px;
                `};
            `}
        `,
    },
    leftSideActions: {
        css: css`
            display: flex;
            margin-top: 10px;
            &:empty {
                display: none;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                gap: unset;
                flex-direction: column;
                height: auto;
            }
        `,
    },
    rightSide: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            max-width: calc(100% - 450px);
            justify-content: space-between;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                margin-top: 18px;
                max-width: unset;
            }
            /* XNG-Changes: FLIP-938 */
            @media print {
                margin-top: 18px;
                max-width: unset;
            }
            ${({ isFullWidth }: any) => css`
                ${isFullWidth &&
                css`
                    flex: 0;
                    min-width: 490px;
                `};
            `}
        `,
    },
    notesFieldSection: {
        ...sharedNotesFieldSectionStyles,
    },
    priceSection: {
        css: css`
            @media print {
                justify-content: flex-end;
            }
            display: flex;
            align-items: center;
            justify-content: space-between;
            ${({ isFullWidth }: any) => css`
                ${isFullWidth &&
                css`
                    min-width: 490px;
                `};
            `};

            ${xScreen("lte", constants.cartLine.breakpoints.xs)} {
                flex-wrap: wrap;
            }
        `,
    },
    rightSideActions: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            height: 18px;
            &:empty {
                display: none;
            }
            ${({ isFullWidth }: any) => css`
                ${isFullWidth &&
                css`
                    min-width: 470px;
                `};
            `}
        `,
    },
    availability: {
        css: css`
            display: flex;
            flex: 1;
            align-items: flex-end;
        `,
    },
    lineNotes: {
        css: css`
            width: 100%;
        `,
    },
    remove: {
        css: css`
            display: flex;
            padding: 0 12px 0 26px;
            min-width: 48px;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                position: absolute;
                right: 8px;
                top: 30px;
                padding: 0;
                min-width: unset;
            }
        `,
    },
    // XNG-Changes: FLIP-818
    price: {
        css: css`
            flex: 1;
            &&&& legend,
            p[data-test-selector="heading"] {
                margin-bottom: 8px;
            }

            ${xScreen("lte", constants.cartLine.breakpoints.xs)} {
                flex: 1 1 100%;
                margin-top: 4px;
                margin-bottom: 12px;
                width: 100%;
            }
            /* XNG-Changes: FLIP-909 */
            @media print {
                max-width: 200px;
            }
        `,
    },
    subtotal: {
        css: css`
            flex: 1;
            & legend,
            p[data-test-selector="heading"] {
                margin-bottom: 8px;
            }
        `,
    },
    qty: {
        css: css`
            flex: 1;
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                margin-top: -3px;
                flex: unset;
            }
        `,
    },
    total: {
        css: css`
            flex: 1;
            & legend,
            p[data-test-selector="heading"] {
                margin-bottom: 8px;
            }
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                margin-left: 32px;
                margin-right: auto;
            }
        `,
    },
    description: {
        css: css`
            display: flex;
            flex-direction: column;
            @media print {
                display: block;
            }
        `,
    },
    errorsSection: {
        ...mergeWithCss(sharedNotesFieldSectionStyles, {
            css: css`
                margin-top: 16px;
                ${({ isFullWidth }: any) => css`
                    ${isFullWidth &&
                    css`
                        max-width: 100%;
                        > div {
                            width: 100%;
                        }
                        ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                            &&& {
                                max-width: 100%;
                            }
                            > div {
                                &&& {
                                    width: 100%;
                                }
                            }
                        }
                    `};
                `}
            `,
        }),
    },
    qtyInfoWrapper: {
        css: css`
            ${xScreen("lte", constants.cartLine.breakpoints.md)} {
                &.qtyInfo-mobile {
                    display: none;
                }
                &.qtyInfo-tablet {
                    display: block;
                }
            }
            ${xScreen("lte", "xs")} {
                &.qtyInfo-mobile {
                    display: block;
                }
                &.qtyInfo-tablet {
                    display: none;
                }
            }
        `,
    },
};

const enum NAMES {
    Image = "Image",
    Description = "Description",
    Price = "Price",
    Subtotal = "Subtotal",
    Qty = "Qty",
    Total = "Total",
    Availability = "Availability",
    AvailabilityLink = "AvailabilityLink",
    AddToList = "AddToList",
    Remove = "Remove",
    LineNotesLink = "LineNotesLink",
    LineNotes = "LineNotes",
    Errors = "Errors",
    MinimumOrderQty = "MinimumOrderQty",
    MultipleSaleQty = "MultipleSaleQty",
}

const getChild = (children: React.ReactElement[], childName: NAMES) =>
    children.find(child => (child.type as React.NamedExoticComponent<any>)?.displayName === childName);

const getDestructeredChildren = (childrenArray: React.ReactElement[]) => {
    return [
        getChild(childrenArray, NAMES.Image),
        getChild(childrenArray, NAMES.Description),
        getChild(childrenArray, NAMES.Price),
        getChild(childrenArray, NAMES.Subtotal),
        getChild(childrenArray, NAMES.Qty),
        getChild(childrenArray, NAMES.Total),
        getChild(childrenArray, NAMES.Availability),
        getChild(childrenArray, NAMES.AvailabilityLink),
        getChild(childrenArray, NAMES.LineNotesLink),
        getChild(childrenArray, NAMES.LineNotes),
        getChild(childrenArray, NAMES.Remove),
        getChild(childrenArray, NAMES.AddToList),
        getChild(childrenArray, NAMES.Errors),
        getChild(childrenArray, NAMES.MinimumOrderQty),
        getChild(childrenArray, NAMES.MultipleSaleQty),
    ];
};

interface RowProps {
    alignItems?: "flex-end" | "center" | "flex-start";
    justifyContent?: "space-between" | "flex-end";
    gap?: number;
}

const CartLineContainer: React.FC<CartLineContainerProps> & OwnProps = ({
    children,
    extendedStyles,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(cartlineContainerStyles, extendedStyles));
    const value = React.useMemo(() => ({ styles }), [styles]);
    const childrenArray = React.Children.toArray(children) as React.ReactElement[];
    const [
        image,
        description,
        price,
        subtotal,
        qty,
        total,
        availability,
        availabilityLink,
        lineNotesLink,
        lineNotes,
        remove,
        addToList,
        errors,
        minimumOrderQty,
        multipleSaleQty,
    ] = React.useMemo(() => getDestructeredChildren(childrenArray), [childrenArray]);
    const className = otherProps.className;
    const isFullWidth = otherProps.isFullWidth;
    return (
        <CartLineContainerContext.Provider value={value}>
            <StyledWrapper
                {...styles.container}
                className={`${containerClassName}-desktop ${className}`}
                {...{ isFullWidth }}
            >
                <StyledWrapper {...styles.topSection} className="top-section">
                    <StyledWrapper {...styles.leftSide} className="left-side" {...{ isFullWidth }}>
                        {image}
                        <StyledWrapper {...styles.leftSideInfo} className="left-side-info">
                            {description}
                            {availability}
                            {minimumOrderQty}
                            {multipleSaleQty}
                            <StyledWrapper {...styles.leftSideActions} className="left-side-actions">
                                {lineNotesLink}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.rightSide} className="right-side" {...{ isFullWidth }}>
                        <StyledWrapper {...styles.priceSection} className="price-section" {...{ isFullWidth }}>
                            {price}
                            {subtotal}
                            {qty}
                            {total}
                            {remove}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.notesFieldSection} className="notes-field-section">
                    {lineNotes}
                </StyledWrapper>
                <StyledWrapper {...styles.errorsSection} className="errors-section" {...{ isFullWidth }}>
                    {errors}
                </StyledWrapper>
            </StyledWrapper>
            {/* Mobile */}
            {/* XNG-Chages: FLIP-818 */}
            <StyledWrapper {...styles.container} className={`${containerClassName}-mobile ${className}`}>
                <StyledWrapper {...styles.leftSide} className="left-side">
                    {image}
                    <StyledWrapper {...styles.leftSideInfo} className="left-side-info">
                        {description}
                        <StyledWrapper {...styles.qtyInfoWrapper} className="qtyInfo-tablet">
                            {minimumOrderQty}
                            {multipleSaleQty}
                        </StyledWrapper>
                        {remove}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSide} className="right-side">
                    <StyledWrapper {...styles.leftSideActions} className="left-side-actions">
                        {availability}
                        {availabilityLink}
                        <StyledWrapper {...styles.qtyInfoWrapper} className="qtyInfo-mobile">
                            {minimumOrderQty}
                            {multipleSaleQty}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.priceSection} className="price-section">
                        {price}
                        {qty}
                        {subtotal}
                        {total}
                    </StyledWrapper>
                    {lineNotesLink}
                    <StyledWrapper {...styles.notesFieldSection} className="notes-field-section">
                        {lineNotes}
                    </StyledWrapper>
                    <StyledWrapper {...styles.errorsSection} className="errors-section">
                        {errors}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </CartLineContainerContext.Provider>
    );
};

// Image
CartLineContainer.Image = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.image} className={`${containerClassName}-image`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Image.displayName = NAMES.Image;

// Description
CartLineContainer.Description = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.description} className={`${containerClassName}-description`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Description.displayName = NAMES.Description;

// Price
CartLineContainer.Price = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.price} className={`${containerClassName}-price`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Price.displayName = NAMES.Price;

// Subtotal
CartLineContainer.Subtotal = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.subtotal} className={`${containerClassName}-subtotal`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Subtotal.displayName = NAMES.Subtotal;

// Qty
CartLineContainer.Qty = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.qty} className={`${containerClassName}-qty`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Qty.displayName = NAMES.Qty;

// Total
CartLineContainer.Total = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.total} className={`${containerClassName}-total`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Total.displayName = NAMES.Total;

// Availability
CartLineContainer.Availability = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.availability} className={`${containerClassName}-availability`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Availability.displayName = NAMES.Availability;

// AvailabilityLink
CartLineContainer.AvailabilityLink = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.availabilityLink} className={`${containerClassName}-availabilityLink`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.AvailabilityLink.displayName = NAMES.AvailabilityLink;

// AddToList
CartLineContainer.AddToList = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.addToList} className={`${containerClassName}-addToList`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.AddToList.displayName = NAMES.AddToList;

// Remove
CartLineContainer.Remove = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.remove} className={`${containerClassName}-remove`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.Remove.displayName = NAMES.Remove;

// LineNotesLink
CartLineContainer.LineNotesLink = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.lineNotesLink} className={`${containerClassName}-lineNotesLink`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.LineNotesLink.displayName = NAMES.LineNotesLink;

// LineNotes
CartLineContainer.LineNotes = ({ children }) => {
    const { styles } = useCartLineContainerContext();
    return (
        <StyledWrapper {...styles.lineNotes} className={`${containerClassName}-lineNotes`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.LineNotes.displayName = NAMES.LineNotes;

// Errors
CartLineContainer.Errors = ({ children }) => {
    return <StyledWrapper className={`${containerClassName}-errors`}>{children}</StyledWrapper>;
};
CartLineContainer.Errors.displayName = NAMES.Errors;

CartLineContainer.MinimumOrderQty = ({ children }) => {
    const { styles } = useCartLineContainerContext();

    return (
        <StyledWrapper {...styles.minimumOrderQty} className={`${containerClassName}-minimumOrderQty`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.MinimumOrderQty.displayName = NAMES.MinimumOrderQty;

CartLineContainer.MultipleSaleQty = ({ children }) => {
    const { styles } = useCartLineContainerContext();

    return (
        <StyledWrapper {...styles.multipleSaleQty} className={`${containerClassName}-multipleSaleQty`}>
            {children}
        </StyledWrapper>
    );
};
CartLineContainer.MultipleSaleQty.displayName = NAMES.MultipleSaleQty;

export default CartLineContainer;
